import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fetchCompaniesApi, createUserApi, fetchUsersApi, fetchUpdateUser, toggleUserActiveApi, deleteUserApi } from '../../services/api';
import { Cargando } from '../../components/Cargando';

export default function Personal() {
  const { token } = useAuth();

  // Listado de personal (local por ahora; se puede cablear a API luego)
  const [people, setPeople] = useState([]);
  const [query, setQuery] = useState('');

  // Modal form state
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState('');
  const [personToDelete, setPersonToDelete] = useState(null);


  const onlyDigits = (s) => String(s || '').replace(/[^0-9]/g, '');

  const [form, setForm] = useState({
    empresaId: '',
    nombres: '',
    apellidos: '',
    identificacion: '', // DNI/PASAPORTE
    email: '',
    password: '',
    accesoWeb: true,
    accesoMovil: true,
    mustFollowOrder: false,
    role: '', // 1=ADMINISTRADOR, 2=SUPERVISOR, 3=VIGILANTE
  });
  const [showPwd, setShowPwd] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const pushToast = (type, msg) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, msg }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  };
  const listUsers = async () => {
    let active = true;
    (async () => {
      try {
        const list = await fetchUsersApi({ token });
        if (!active) return;
        setPeople(list);
      } catch {
      }
      finally {
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }

  useEffect(() => {
    let active = true;
    (async () => {
      setCompaniesLoading(true);
      setCompaniesError('');
      try {
        const list = await fetchCompaniesApi({ token });
        if (!active) return;
        setCompanies(list.map((t) => ({ id: t.id ?? t.value ?? t.code, name: t.name ?? t.descripcion ?? String(t.id ?? t.code ?? '') })));
      } catch (e) {
        if (!active) return;
        setCompaniesError('No se pudieron cargar empresas');
      } finally {
        if (active) setCompaniesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  // Cargar usuarios inicialmente
  useEffect(() => {
    listUsers()
  }, [token]);

  const resetForm = () => setForm({
    empresaId: '',
    nombres: '',
    apellidos: '',
    identificacion: '',
    email: '',
    password: '',
    accesoWeb: true,
    accesoMovil: true,
    mustFollowOrder: false,
    role: '',
  });

  const openCreate = () => {
    setEditingIndex(null);
    resetForm();
    setShowForm(true);
  };

  const openEdit = (idx) => {
    setEditingIndex(idx);
    const p = people[idx];
    setForm({
      empresaId: p.empresaId || '',
      nombres: p.nombres.trim() || '',
      apellidos: p.apellidos.trim() || '',
      identificacion: p.identificacion.trim() || '',
      email: p.email.trim() || '',
      password: '', // por seguridad no pre-cargamos
      accesoWeb: !!p.accesoWeb,
      accesoMovil: !!p.accesoMovil,
      mustFollowOrder: !!(p.mustFollowOrder ?? p.mustfolloworder ?? false),
      role: String(p.role ?? p.rol ?? ''),
    });
    setShowForm(true);
  };

  const deletePerson = (p) => {
    setPersonToDelete(p)
  }

  const formDelete = async (e) => {
    e.preventDefault();

    if (!personToDelete) return;

    try {
      await deleteUserApi({ id: personToDelete.id, token });

      // Quitar visualmente de la lista
      // setPeople(prev => prev.filter(x => x.id !== personToDelete.id));
      listUsers()
      pushToast('success', `Eliminado correctamente`);

    } catch (err) {
      pushToast('danger', err?.message || 'Error al eliminar');
    }

    setPersonToDelete(null);
  };


  const closeForm = () => {
    setShowForm(false);
    setEditingIndex(null);
    resetForm();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.role) { pushToast('danger', 'Seleccione un rol'); return; }
    try {
      if (editingIndex == null) {
        const res = await createUserApi({
          token,
          dto: {
            companyId: payload.empresaId,
            name: payload.nombres,
            lastName: payload.apellidos,
            identification: payload.identificacion,
            email: payload.email,
            password: payload.password,
            accessWeb: !!payload.accesoWeb,
            accessAppMovil: !!payload.accesoMovil,
            mustFollowOrder: !!payload.mustFollowOrder,
            role: Number(payload.role),
          },
        });
        const ok = !!res?.isSuccess;
        if (ok) {
          pushToast('success', 'Personal creado correctamente');
          // Recargar desde backend para reflejar datos reales
          try {
            const list = await fetchUsersApi({ token });
            setPeople(list);
          } catch { }
        } else {
          throw new Error(res?.message || 'No se pudo crear el personal');
        }
      } else {
        const current = people[editingIndex];
        const userId = current?.id ?? current?.userId ?? current?.UserId;
        if (!userId) {
          throw new Error('No se pudo determinar el ID del usuario.');
        }
        await fetchUpdateUser({
          id: userId,
          name: payload.nombres,
          lastName: payload.apellidos,
          identification: payload.identificacion,
          email: payload.email.trim(),
          role: Number(payload.role),
          accessWeb: !!payload.accesoWeb,
          accessAppMovil: !!payload.accesoMovil,
          mustFollowOrder: !!payload.mustFollowOrder,
          password: payload.password.trim(),
          companyId: payload.empresaId,
          token,
        });
        pushToast('success', 'Personal actualizado');
        try {
          const list = await fetchUsersApi({ token });
          setPeople(list);
        } catch { }
      }
      setShowForm(false);
      setEditingIndex(null);
      resetForm();
    } catch (err) {
      pushToast('danger', err?.message || 'Error al registrar personal');
    }
  };

  const roleLabel = (val) => {
    const v = Number(val);
    return v === 1 ? 'ADMINISTRADOR' : v === 2 ? 'SUPERVISOR' : v === 3 ? 'VIGILANTE' : '';
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) =>
      `${p.nombres} ${p.apellidos}`.toLowerCase().includes(q) ||
      String(p.identificacion || '').toLowerCase().includes(q) ||
      roleLabel(p.role ?? p.rol).toLowerCase().includes(q)
    );
  }, [people, query]);

  // estilos de switch llamativo
  const SWITCH_STYLES = `
  .gp-switch { display: inline-flex; align-items: center; gap: 8px; }
  .gp-switch-label { font-weight: 600; font-size: 0.9rem; }
  .gp-switch-root { position: relative; display: inline-block; appearance: none; background: transparent; border: none; padding: 0; cursor: pointer; }
  .gp-switch-track { display: block; width: 64px; height: 30px; border-radius: 15px; background: #dc3545; transition: background .2s ease; box-shadow: inset 0 0 0 2px rgba(0,0,0,.05); }
  .gp-switch-root.on .gp-switch-track { background: #28a745; }
  .gp-switch-knob { position: absolute; top: 2px; left: 2px; width: 26px; height: 26px; border-radius: 50%; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,.2); transition: left .2s ease; }
  .gp-switch-root.on .gp-switch-knob { left: 36px; }
  .gp-switch-input { position: absolute; opacity: 0; width: 1px; height: 1px; pointer-events: none; }
  .gp-card { position: relative; }
  .gp-status-dot { position: absolute; top: 8px; right: 8px; width: 10px; height: 10px; border-radius: 50%; box-shadow: 0 0 0 2px #fff; }
  .gp-status-dot.on { background: #28a745; }
  .gp-status-dot.off { background: #dc3545; }
  .gp-device-icon { font-size: 1.3rem; font-weight: 700; line-height: 1; }
  `;

  return (
    <div className="container-fluid">
      <div className="gp-toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast align-items-center text-bg-${t.type} show`} role="alert" aria-live="assertive" aria-atomic="true">
            <div className="d-flex">
              <div className="toast-body">{t.msg}</div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))} aria-label="Close"></button>
            </div>
          </div>
        ))}
      </div>
      <style>{SWITCH_STYLES}</style>
      <div className="mb-2">
        <h2 className="mb-2 text-center">PERSONAL</h2>
      </div>

      <div className="mb-3 d-flex gap-2 justify-content-end">
        <button className="btn btn-primary" title="Agregar" onClick={openCreate}><i className="bi bi-plus-lg" /></button>
      </div>

      {/* Buscar */}
      <div className="mb-3">
        <input
          className="form-control"
          placeholder="Buscar por nombre, identificacion o rol"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Cards */}
      <div className="row g-3">
        {loading &&
          <div className="col-12 text-center text-muted py-4"><Cargando /></div>
        }
        {filtered.length === 0 && !loading && (
          <div className="col-12 text-center text-muted py-4">Sin registros</div>
        )}
        {filtered.map((p, idx) => (
          <div key={p.id || idx} className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div className="card h-100 gp-card">
              <span className={`gp-status-dot ${p.isActive ? 'on' : 'off'}`} />
              <div className="card-body">
                <div className="fw-semibold mb-1">
                  <i className="bi bi-person text-primary me-2" />
                  {p.apellidos}, {p.nombres}
                </div>
                <div className="text-muted small">
                  <i className="bi bi-person-vcard text-info me-2" />
                  Identificación: {p.identificacion}
                </div>
                <div className="text-muted small">
                  <i className="bi bi-envelope-fill text-danger me-2" />
                  Email: {p.email}
                </div>
                <div className="text-muted small">
                  <i className="bi bi-person-badge text-warning me-2" />
                  Rol: {roleLabel(p.role ?? p.rol)}
                </div>
                {p.companyName && (
                  <div className="text-muted small">
                    <i className="bi bi-building text-success me-2" />
                    Empresa: {p.companyName}
                  </div>
                )}
              </div>
              <div className="card-footer d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <i className={`bi bi-laptop gp-device-icon ${p.accesoWeb ? 'text-success' : 'text-danger'}`} title="Acceso web" />
                  <i className={`bi bi-phone gp-device-icon ${p.accesoMovil ? 'text-success' : 'text-danger'}`} title="Acceso móvil" />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button className='btn btn-sm btn-outline-danger ' data-bs-toggle="modal" data-bs-target="#deletepersonal"
                    title="Eliminar"
                    onClick={() => deletePerson(p)}>
                    <i className='bi-trash3' />
                  </button>
                  <button
                    className={`btn btn-sm ${p.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                    title={p.isActive ? 'Desactivar' : 'Activar'}
                    onClick={async () => {
                      try {
                        // Enviar el estado invertido: si está activo => false (activar), si está inactivo => true (desactivar)
                        const state = !p.isActive;
                        await toggleUserActiveApi({ id: p.id, state, token });
                        const list = await fetchUsersApi({ token });
                        setPeople(list);
                        pushToast('success', 'Usuario actualizado');
                      } catch (e) {
                        pushToast('danger', e?.message || 'No se pudo actualizar');
                      }
                    }}
                  >
                    <i className={`bi ${p.isActive ? 'bi-toggle-off' : 'bi-toggle-on'}`} />

                  </button>
                  <button className="btn btn-sm btn-outline-primary" title="Editar" onClick={() => openEdit(idx)}><i className="bi bi-pencil" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal crear/editar */}
      <div className={`modal fade ${showForm ? 'show d-block' : ''}`} tabIndex="-1" role="dialog" aria-modal={showForm}>
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <form onSubmit={onSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">{editingIndex == null ? 'Agregar Personal' : 'Editar Personal'}</h5>
                <button type="button" className="btn-close" onClick={closeForm} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                {/* EMPRESA */}
                <div className="mb-3">
                  <label className="form-label">EMPRESA</label>
                  <select
                    className="form-select"
                    value={form.empresaId}
                    onChange={(e) => setForm({ ...form, empresaId: e.target.value })}
                    required
                  // disabled={editingIndex != null}
                  >
                    <option value="">Seleccionar...</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {companiesLoading && <div className="form-text">Cargando empresas...</div>}
                  {companiesError && <div className="text-danger small">{companiesError}</div>}
                </div>
                {/* NOMBRES */}
                <div className="mb-3">
                  <label className="form-label">NOMBRES</label>
                  <input className="form-control" value={form.nombres} onChange={(e) => setForm({ ...form, nombres: e.target.value })} required />
                </div>
                {/* APELLIDOS */}
                <div className="mb-3">
                  <label className="form-label">APELLIDOS</label>
                  <input className="form-control" value={form.apellidos} onChange={(e) => setForm({ ...form, apellidos: e.target.value })} required />
                </div>
                {/* DNI/PASAPORTE */}
                <div className="mb-3">
                  <label className="form-label">DNI/PASAPORTE</label>
                  <input className="form-control" value={form.identificacion} onChange={(e) => setForm({ ...form, identificacion: onlyDigits(e.target.value) })} required />
                </div>
                {/* EMAIL */}
                <div className="mb-3">
                  <label className="form-label">EMAIL</label>
                  <input type="email" className="form-control" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </div>
                {/* PASSWORD + OJITO */}
                {(
                  <div className="mb-3">
                    <label className="form-label">PASSWORD</label>
                    <div className="input-group">
                      <input
                        type={showPwd ? 'text' : 'password'}
                        className="form-control"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required={editingIndex == null}
                      />
                      <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPwd((v) => !v)} title={showPwd ? 'Ocultar' : 'Mostrar'}>
                        <i className={`bi ${showPwd ? 'bi-eye-slash' : 'bi-eye'}`} />
                      </button>
                    </div>
                    <p className='my-1 small text-danger fw-semibold'>{editingIndex !== null ? "Crear nueva contrseña (Opcional)" : ""}</p>

                  </div>
                )}

                {/* ROL */}
                <div className="mb-2">
                  <label className="form-label">ROL</label>
                  <select className="form-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    required>
                    <option value="">Seleccionar...</option>
                    <option value="2">SUPERVISOR</option>
                    <option value="3">VIGILANTE</option>
                  </select>
                  {editingIndex != null && <div className="form-text">El rol no puede modificarse en edición.</div>}
                </div>
                {/* ACCESOS como switches estilizados */}
                <div className="mb-3 d-flex align-items-center gap-4 flex-wrap">
                  <div className="gp-switch">
                    <span className="gp-switch-label">ACCESO WEB</span>
                    <input id="acc-web" className="gp-switch-input" type="checkbox" checked={form.accesoWeb} onChange={(e) => setForm({ ...form, accesoWeb: e.target.checked })} />
                    <label htmlFor="acc-web" className={`gp-switch-root ${form.accesoWeb ? 'on' : ''}`} title={form.accesoWeb ? 'Permitido' : 'No permitido'}>
                      <span className="gp-switch-track"></span>
                      <span className="gp-switch-knob"></span>
                    </label>
                  </div>
                  <div className="gp-switch">
                    <span className="gp-switch-label">ACCESO MOVIL</span>
                    <input id="acc-mov" className="gp-switch-input" type="checkbox" checked={form.accesoMovil} onChange={(e) => setForm({ ...form, accesoMovil: e.target.checked })} />
                    <label htmlFor="acc-mov" className={`gp-switch-root ${form.accesoMovil ? 'on' : ''}`} title={form.accesoMovil ? 'Permitido' : 'No permitido'}>
                      <span className="gp-switch-track"></span>
                      <span className="gp-switch-knob"></span>
                    </label>
                  </div>
                  <div className="gp-switch">
                    <span className="gp-switch-label">ORDEN</span>
                    <input id="must-order" className="gp-switch-input" type="checkbox" checked={form.mustFollowOrder} onChange={(e) => setForm({ ...form, mustFollowOrder: e.target.checked })} />
                    <label htmlFor="must-order" className={`gp-switch-root ${form.mustFollowOrder ? 'on' : ''}`} title={form.mustFollowOrder ? 'Debe seguir orden' : 'Sin orden obligatoria'}>
                      <span className="gp-switch-track"></span>
                      <span className="gp-switch-knob"></span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeForm}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal Eliminar*/}
      <div className="modal fade" id="deletepersonal" tabIndex="-1" aria-labelledby="deletepersonal" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className='modal-title'>Eliminar personal</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form onSubmit={(e) => formDelete(e)}>
              <div className="modal-body">
                {personToDelete ? (
                  <>
                    <p className='fw-semibold mb-1'>  ¿Estás seguro de eliminar a <b>{personToDelete.nombres} {personToDelete.apellidos}</b>?</p>
                  </>
                ) : ""}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                <button type="submit" className="btn btn-danger" data-bs-dismiss="modal">Eliminar</button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {showForm && <div className="modal-backdrop fade show" onClick={closeForm} />}
    </div>
  );
}
